import { Node, mergeAttributes } from '@tiptap/core';

/**
 * TipTap node for whitelisted iframe embeds (Scribe, YouTube, Loom, Vimeo).
 *
 * The rendered <iframe> is wrapped in a positioned div so the embed is
 * responsive (aspect-ratio ~ 62% by default). The final HTML shape must
 * match what the backend sanitizer emits so what the admin sees in the
 * editor is exactly what the reader sees on the public page.
 *
 * Accepts either a full <iframe> HTML paste (we extract the src) or a
 * bare URL (we point src at it directly).
 */
const ALLOWED_HOSTS = [
  'scribehow.com',
  'youtube.com', 'youtube-nocookie.com', 'youtu.be',
  'loom.com',
  'vimeo.com', 'player.vimeo.com',
];

export function isAllowedEmbedUrl(url) {
  try {
    const u = new URL(url);
    if (!/^https?:$/.test(u.protocol)) return false;
    const h = u.hostname.toLowerCase();
    return ALLOWED_HOSTS.some(a => h === a || h.endsWith('.' + a));
  } catch { return false; }
}

// Normalise a paste — accepts either a URL or an <iframe src="..."> snippet.
// Returns the extracted src, or null.
export function extractEmbedSrc(input) {
  if (!input) return null;
  const trimmed = input.trim();
  const iframeMatch = trimmed.match(/<iframe[^>]*\ssrc\s*=\s*["']([^"']+)["'][^>]*>/i);
  if (iframeMatch) return iframeMatch[1];
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return null;
}

export const Iframe = Node.create({
  name: 'iframe',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      title: { default: 'Embedded content' },
    };
  },

  parseHTML() {
    return [
      // Match a fully-formed responsive wrapper we produced OR a bare iframe.
      { tag: 'div.reachly-embed', getAttrs: (el) => {
          const iframe = el.querySelector('iframe');
          if (!iframe) return false;
          return {
            src: iframe.getAttribute('src'),
            title: iframe.getAttribute('title') || 'Embedded content',
          };
        }},
      { tag: 'iframe[src]', getAttrs: (el) => ({
          src: el.getAttribute('src'),
          title: el.getAttribute('title') || 'Embedded content',
        })},
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const src = HTMLAttributes.src || '';
    const title = HTMLAttributes.title || 'Embedded content';
    return [
      'div',
      mergeAttributes({
        class: 'reachly-embed',
        // Inline style keeps embeds responsive even when consumed outside
        // Tailwind (email previews, RSS readers, etc.).
        style: 'position:relative;padding-bottom:62%;height:0;overflow:hidden;border-radius:8px;border:1px solid #27272a;margin:16px 0;',
      }),
      [
        'iframe',
        mergeAttributes({
          src,
          title,
          loading: 'lazy',
          allow: 'fullscreen',
          referrerpolicy: 'no-referrer',
          style: 'position:absolute;top:0;left:0;width:100%;height:100%;border:0;',
        }),
      ],
    ];
  },

  addCommands() {
    return {
      setIframe: (attrs) => ({ chain }) =>
        chain().insertContent({ type: this.name, attrs }).run(),
    };
  },
});

export default Iframe;
